import { t } from "../../i18n";
import { useEffect, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ENGINE_VERSION } from "@tasduvar/engine";
import { TRASH_DAYS } from "@tasduvar/shared";
import { BillingCard } from "../../billing/BillingCard";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { CadFormContext } from "../../ui/formContext";
import { useCadUi } from "../../state/cadUiStore";
import { CadShell } from "../cad/CadShell";
import { DOC_LINKS } from "../cad/DocSheet";
import { NewProjectForm } from "./NewProjectForm";
import { RecentProjects } from "./RecentProjects";
import { BulkBar, SelectAllBox, useSelection } from "./selection";
import { useHomeProjects } from "./useHomeProjects";

const WHAT: string[] = [
  t("Taş ağırlık, betonarme konsol (T/L) ve payandalı duvar; TS 500 / EN 1992-1-1 / ACI 318-19 kesit tasarımı."),
  t("TBDY 2018, Eurocode 7/8-5 ve ABD (ASD) hedefleri aynı girdi üzerinde."),
  t("Mononobe-Okabe ile statik ve dinamik itki; her adımın formülü raporda."),
  t("Metraj ve keşif: ÇŞB poz numaralı birim fiyat tablosu, XLSX/CSV."),
  t("Zemin etüt raporundan (PDF) otomatik girdi okuma — yapay zekâsız, tarayıcıda."),
];

/** Beyaz panel: 24 px başlık bandı + gövde (README §3 Başlangıç paftası). `region` = erişilebilir ad başlık. */
function Panel({ title, label, children, className = "" }: { title: string; label?: string; children: ReactNode; className?: string }): JSX.Element {
  return (
    <section aria-label={label ?? title} className={`bg-white border border-cad-line ${className}`}>
      <div className="cad-head">{title}</div>
      {children}
    </section>
  );
}

/**
 * CAD "Başlangıç" paftası (README §3; Cem 2026-09-21 karar a — giriş yapan kullanıcıya): BAŞLA · PROJELERİM (bulut) ·
 * BU CİHAZDAKİ TASLAKLAR · NE YAPAR · BAĞLANTILAR. Yeni proje künyesi CadDialog'da açılır (`cadUi.dialog === "new"`).
 * e2e: "Bulut projelerim" region, "Yeni proje" düğmesi, "Proje Aç" dosya girdisi vitrinle aynı adlarda.
 */
export function StartPage(): JSX.Element {
  // Yeni proje künyesi `cadUi.dialog === "new"` ile açılır (QAT "Yeni proje" da aynı diyaloğu açar)
  const dialog = useCadUi((s) => s.dialog), setDialog = useCadUi((s) => s.setDialog);
  const showForm = dialog === "new", setShowForm = (v: boolean): void => setDialog(v ? "new" : null);
  const { subscription, local, cloud, trash, restoreCloud, purgeCloud, error, maxProjects, limitHit, closeLimit, atLimit, exportCloud, fileRef, canCreate, create, tryShowcase, onFile, openLocal, openCloud, removeLocal, removeCloud, moveToCloud } = useHomeProjects();
  // "Yeni proje" (buradan, QAT'tan ya da komuttan) sınır doluysa künye yerine uyarı açar; liste yüklenince denetlenir
  useEffect(() => { if (showForm && cloud !== null && atLimit()) setDialog(null); }, [showForm, cloud]);
  const trashSel = useSelection(trash.map((p) => p.id));
  const purge = (ids: string[], what: string): void => { if (window.confirm(`${what} ${t("kalıcı olarak silinsin mi? Bu işlem geri alınamaz.")}`)) void purgeCloud(ids); };
  return (
    <CadShell current="start">
      <CadFormContext.Provider value={true}>
        <div className="flex-1 min-h-0 min-w-0 overflow-auto bg-cad-off p-4 grid gap-4 content-start lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]" aria-label={t("Başlangıç paftası")}>
          <div className="grid gap-3 content-start min-w-0">
            <Panel title={t("Başla")}>
              <div className="p-3 flex flex-wrap items-center gap-2">
                <button type="button" className="cad-btn-primary !h-auto !px-3 !py-2 font-medium" disabled={!canCreate} onClick={() => setShowForm(true)}>{t("Yeni proje")}</button>
                <label className={`cad-btn-box !h-auto !px-3 !py-2 ${canCreate ? "" : "opacity-50 pointer-events-none"}`}>
                  {t("Dosyadan aç…")}
                  <input ref={fileRef} type="file" accept=".json,application/json" className="sr-only" aria-label={t("Proje Aç")} disabled={!canCreate} onChange={(e) => void onFile(e.target.files?.[0])} />
                </label>
                <button type="button" className="cad-btn-box !h-auto !px-3 !py-2" disabled={!canCreate} onClick={tryShowcase}>{t("Örnek projeyle dene")}</button>
                <span className="ml-auto text-cad-ink3">{t("Hesaplar tarayıcınızda yapılır")}{t("; projeleriniz sunucuda şifreli bağlantıyla saklanır.")}</span>
              </div>
              {error ? <p role="alert" className="mx-3 mb-3 px-2 py-1 bg-cad-badbg text-[#B42318] text-[11px]">{error}</p> : null}
              {subscription && !subscription.canWrite ? <div className="px-3 pb-3"><BillingCard sub={subscription} /></div> : null}
            </Panel>
            <Panel title={maxProjects !== null && cloud ? `${t("Projelerim")} · ${cloud.length} / ${maxProjects}` : t("Projelerim")} label={t("Bulut projelerim")}>
              {cloud === null ? <p className="m-0 p-2 text-cad-ink3">{t("Yükleniyor…")}</p> : <RecentProjects items={cloud} onOpen={openCloud} onDelete={removeCloud} deleteHint={`${t("Silinenler bölümüne taşınır;")} ${TRASH_DAYS} ${t("gün içinde geri alabilirsiniz.")}`}
                extra={(id) => <Button variant="ghost" className="!h-5 mr-1" onClick={() => void exportCloud(id)} aria-label={t("Dışa aktar (JSON)")}>{t("Dışa aktar")}</Button>} />}
              <p className="m-0 px-2 py-1 border-t border-cad-soft2 text-[10px] text-cad-ink3">{t("bulut, tüm cihazlarınızda")}</p>
            </Panel>
            {trash.length ? (
              <Panel title={`${t("Silinenler")} · ${trash.length}`} label={t("Silinen projeler")}>
                <BulkBar s={trashSel} cad>
                  <Button variant="ghost" className="!h-5" onClick={() => void restoreCloud(trashSel.selected)}>{t("Seçilenleri geri yükle")}</Button>
                  <Button variant="danger" className="!h-5" onClick={() => purge(trashSel.selected, `${trashSel.selected.length} ${t("proje")}`)}>{t("Seçilenleri kalıcı sil")}</Button>
                </BulkBar>
                <table className="w-full text-[11px] border-collapse">
                  <thead><tr className="bg-cad-head text-cad-ink2"><th className="px-2 py-0.5 w-5"><SelectAllBox s={trashSel} /></th><th className="px-2 py-0.5 font-semibold text-left">{t("Proje")}</th><th className="px-2 py-0.5 font-semibold text-left">{t("Kalıcı silinmesine")}</th><th /></tr></thead>
                  <tbody>{trash.map((p) => {
                    const left = Math.max(0, TRASH_DAYS - Math.floor((Date.now() - new Date(p.deletedAt).getTime()) / 86_400_000));
                    return (
                      <tr key={p.id} className="border-b border-cad-soft2">
                        <td className="px-2 py-[3px]"><input type="checkbox" className="align-middle" checked={trashSel.has(p.id)} onChange={() => trashSel.toggle(p.id)} aria-label={`${p.name} ${t("projesini seç")}`} /></td>
                        <td className="px-2 py-[3px] text-cad-ink2">{p.name}</td>
                        <td className="px-2 py-[3px] font-mono">{left} {t("gün")}</td>
                        <td className="px-2 py-px text-right whitespace-nowrap">
                          <Button variant="ghost" className="!h-5 mr-1" onClick={() => void restoreCloud([p.id])} aria-label={`${p.name} ${t("projesini geri al")}`}>{t("Geri yükle")}</Button>
                          <Button variant="danger" className="!h-5" onClick={() => purge([p.id], `"${p.name}"`)} aria-label={`${p.name} ${t("projesini kalıcı sil")}`}>{t("Kalıcı sil")}</Button>
                        </td>
                      </tr>
                    );
                  })}</tbody>
                </table>
                <p className="m-0 px-2 py-1 border-t border-cad-soft2 text-[10px] text-cad-ink3">{t("Silinen projeler")} {TRASH_DAYS} {t("gün sonra sürüm geçmişi ve paylaşım bağlantılarıyla birlikte kalıcı silinir; proje sınırına sayılmaz.")}</p>
              </Panel>
            ) : null}
            <Panel title={t("Bu cihazdaki taslaklar")}>
              <RecentProjects items={local} onOpen={openLocal} onDelete={removeLocal}
                extra={canCreate ? (id) => <Button variant="ghost" className="!h-5 mr-1" onClick={() => void moveToCloud(id)} aria-label={t("Buluta taşı")}>{t("Buluta taşı")}</Button> : undefined} />
              <p className="m-0 px-2 py-1 border-t border-cad-soft2 text-[10px] text-cad-ink3">{t("yalnız bu tarayıcıda")}</p>
            </Panel>
          </div>
          <div className="grid gap-3 content-start min-w-0">
            <Panel title={t("Ne yapar")}>
              <ul className="m-0 py-2.5 pr-3 pl-[26px] grid gap-1.5 text-cad-ink2 leading-[1.45]">{WHAT.map((s) => <li key={s}>{s}</li>)}</ul>
            </Panel>
            <Panel title={t("Bağlantılar")}>
              <div className="px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-cad-accent">
                {DOC_LINKS.map((d) => <Link key={d.to} to={d.to} className="hover:underline">{d.label}</Link>)}
                <Link to="/planlar" className="hover:underline">{t("Planlar")}</Link>
                <span className="ml-auto font-mono text-cad-ink3">{t("Hesap motoru v")}{ENGINE_VERSION}</span>
              </div>
            </Panel>
          </div>
        </div>
        <Dialog open={limitHit !== null} onClose={closeLimit} label={t("Proje sınırı")} width={460} tone="warn" footer={<button type="button" className="cad-btn-primary" onClick={closeLimit}>{t("Tamam")}</button>}>
          <h2>{t("Proje sınırına ulaştınız")} ({limitHit} / {limitHit})</h2>
          <p className="text-[11px] text-cad-ink2">{t("Yeni proje açmak için önce en az 1 projenizi dışa aktarın ve silin.")} {t("Dışa aktarılan JSON dosyasını istediğiniz zaman \"Dosyadan aç…\" ile geri yükleyebilirsiniz.")}</p>
        </Dialog>
        <Dialog open={showForm} onClose={() => setShowForm(false)} label={t("Yeni proje")} width={680}>
          <NewProjectForm onCancel={() => setShowForm(false)}
            onCreate={(info, seismic) => { useCadUi.getState().setLayout("model"); create(info, seismic); }}
            onCreateSmart={(info, seismic, start) => { useCadUi.getState().setLayout("design"); useCadUi.getState().setRibbonTab("home"); create(info, seismic, start); }} />
        </Dialog>
      </CadFormContext.Provider>
    </CadShell>
  );
}
